// ============================================================
// VCS.Signaling — WebSocket-Verbindung & Message-Dispatcher
// ============================================================
window.VCS = window.VCS || {};

VCS.Signaling = class {

    // ── Konstanten ───────────────────────────────────────────

    static WS_URL = `wss://${location.host}/ws`;

    // Nur ein Notnagel fuer die Sekunden bis der Server per 'turn-credentials'
    // die echte Liste schickt (zweimal: einmal beim Connect, einmal nach der
    // Chat-Anmeldung, dann nach Naehe sortiert -- siehe buildIceServers() in
    // signaling/main-server.js). Hier steht bewusst nur STUN: die TURN-Zugaenge
    // brauchen ein HMAC-Credential, das nicht im ausgelieferten JS liegen darf.
    static ICE_SERVERS = [
        {urls: ['stun:turn.secondradio.de:3478']},
    ];

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // View-Cache
        this._view = {
            preStatus          : $('#preStatus'),
            tabCallStatus      : $('#tabCallStatus'),
            hostWaiting        : $('#hostWaiting'),
            guestWaiting       : $('#guestWaiting'),
            approvalName       : $('#approvalName'),
            approvalOverlay    : $('#approvalOverlay'),
            deniedOverlay      : $('#deniedOverlay'),
            remoteCamOff       : $('#remoteCamOff'),
            remoteMuteIndicator: $('#remoteMuteIndicator'),
            tabContacts        : $('#tabContacts'),
        };
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    // WebSocket-Verbindung herstellen
    connect() {
        const s = VCS.state;
        if (s.ws && (s.ws.readyState === WebSocket.OPEN || s.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const ws = new WebSocket(VCS.Signaling.WS_URL);
        s.ws = ws;

        ws.onopen = () => {
            s.reconnectAttempts = 0;
            if (s.reconnectTimer) {
                clearTimeout(s.reconnectTimer);
                s.reconnectTimer = null;
            }
            this._updateStatusBadge(t('connected'), 'prejoin-status--ok');

            // Gepufferte Frontend-Fehler an Backend senden
            if (VCS._errorLoggerFlush) VCS._errorLoggerFlush();

            // Gast-Anruf: erst mit dem Gast-Schluessel anmelden. "register" folgt
            // auf "guest-authed" (guest-app.js onAuthed) -- vorher kennt der
            // Server weder Raum noch Namen des Gastes.
            if (s.guestKey) {
                this.send({ type: 'guest-auth', guestKey: s.guestKey });
            } else if (s.pendingRegisterOnOpen && s.myName) {
                this.send(this._buildRegisterMessage());
                s.pendingRegisterOnOpen = false;
            }
            VCS.captions?.sendConfig();
            VCS.notify?.sendPushSubscription();

            // Ephemeral TURN Credentials anfordern
            this.send({type: 'turn-credentials'});

            // Chat-Auth senden falls registriert
            if (s.chatMyPhone) {
                this.send({
                              type : 'chat-auth',
                              phone: s.chatMyPhone,
                              token: s.chatAuthToken,
                          });
            }
        };

        ws.onclose = () => {
            // Nur aufraeumen, wenn dieser Socket noch der aktive ist. Ein
            // schneller Hintergrund/Vordergrund-Wechsel kann bereits einen
            // neuen Socket gesetzt haben -- dessen Zustand duerfen wir nicht
            // mit dem Close des alten Sockets ueberschreiben.
            if (s.ws && s.ws !== ws) {
                return;
            }
            s.ws = null;
            VCS.captions?.releasePipeline();
            VCS.captions?.clearTranslated();
            VCS.captions?.clearSelfTranscript();
            // Live-Unterhaltung: Verbindungsabbruch mitten im Gespraech --
            // Capture stoppen, letzten Text sichtbar lassen, Retry anbieten.
            VCS.liveConvo?.onConnectionLost();
            VCS.interpretRoom?.onConnectionLost?.();
            if (s.sessionState.phase === 'waiting' || s.sessionState.phase === 'call') {
                s.pendingRegisterOnOpen = true;
                s.sessionState.phase = 'waiting';
                VCS.session?.save();
                VCS.nav?.showScreen('waitingScreen');
                this._view.hostWaiting.addClass('hidden');
                this._view.guestWaiting.removeClass('hidden');
                if (s.suppressRejoinHint) {
                    VCS.session?.setRejoinHint(false);
                    s.suppressRejoinHint = false;
                } else {
                    VCS.session?.setRejoinHint(true);
                }
            }
            // Kein Reconnect solange die App im Hintergrund liegt und wir die
            // WS bewusst getrennt haben -- der Vordergrund-Handler baut sie
            // wieder auf. Sonst wuerde der Backoff-Loop unnoetig weiterlaufen.
            if (!s.presenceHidden) {
                this.scheduleReconnect();
            }
        };

        ws.onerror = () => {
            // Kein Logging — iOS/Safari liefert nur {isTrusted:true}, Reconnect folgt automatisch
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                void this._handleMessage(msg);
            } catch (e) {
                console.error('WS parse error:', e, event.data);
            }
        };
    }

    // JSON-Nachricht an Server senden
    send(obj) {
        if (VCS.state.ws && VCS.state.ws.readyState === WebSocket.OPEN) {
            VCS.state.ws.send(JSON.stringify(obj));
        }
    }

    // Meldet dem Server, welcher Chat auf diesem Geraet gerade offen ist
    // (Server: case "chat-active", null = keiner). Fuer genau diesen Chat
    // laesst der Server den Web-Push an dieses Geraet weg -- online allein
    // heisst nicht gelesen. Laeuft bei jedem Chat-Wechsel und nach jedem
    // "chat-authed" (Reconnect, nach Hintergrund).
    sendActiveChat() {
        this.send({
            type  : 'chat-active',
            id    : VCS.state.myId || null,
            chatId: VCS.state.chatActiveChatId || null,
        });
    }

    // Client-Version ans Signal-Log melden (Server: case "client-info").
    // Laeuft nach jedem "chat-authed", also bei jedem Start und Reconnect.
    _sendClientInfo() {
        Promise.all([this._getClientVersion(), this._getClientEnv()])
            .then(([version, env]) => {
                this.send({ type: 'client-info', app: 'pwa', version, ...env });
            });
    }

    // Browser, Betriebssystem, Geraet, Sprache usw. fuers Signal-Log.
    // Chromium (Chrome, Edge, Samsung, Opera) liefert ueber Client Hints die
    // genauen Werte -- der User-Agent ist dort eingefroren (Android immer
    // "10; K", Windows 11 meldet sich als "NT 10.0"). Safari und Firefox
    // kennen keine Client Hints, dort bleibt der User-Agent.
    async _getClientEnv() {
        const ua = navigator.userAgent || '';
        const env = { browser: '?', browserVersion: '?', os: '?', osVersion: '?' };
        let m;

        if ((m = ua.match(/EdgA?\/([\d.]+)|EdgiOS\/([\d.]+)/))) {
            env.browser = 'Edge'; env.browserVersion = m[1] || m[2];
        } else if ((m = ua.match(/SamsungBrowser\/([\d.]+)/))) {
            env.browser = 'Samsung Internet'; env.browserVersion = m[1];
        } else if ((m = ua.match(/OPR\/([\d.]+)/))) {
            env.browser = 'Opera'; env.browserVersion = m[1];
        } else if ((m = ua.match(/(?:Firefox|FxiOS)\/([\d.]+)/))) {
            env.browser = 'Firefox'; env.browserVersion = m[1];
        } else if ((m = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/))) {
            env.browser = 'Chrome'; env.browserVersion = m[1];
        } else if ((m = ua.match(/Version\/([\d.]+).*Safari/))) {
            env.browser = 'Safari'; env.browserVersion = m[1];
        }

        if ((m = ua.match(/(iPhone|iPad|iPod).*? OS (\d+(?:_\d+)*)/))) {
            env.os = m[1] === 'iPad' ? 'iPadOS' : 'iOS';
            env.osVersion = m[2].replace(/_/g, '.');
            env.device = m[1];
        } else if ((m = ua.match(/Android (\d+(?:\.\d+)*)/))) {
            env.os = 'Android'; env.osVersion = m[1];
        } else if ((m = ua.match(/Windows NT (\d+\.\d+)/))) {
            env.os = 'Windows'; env.osVersion = m[1] === '10.0' ? '10/11' : 'NT ' + m[1];
        } else if ((m = ua.match(/Mac OS X (\d+(?:[_.]\d+)*)/))) {
            // iPad im Desktop-Modus meldet sich als Mac -- Touch verraet es.
            if (navigator.maxTouchPoints > 1) {
                env.os = 'iPadOS'; env.device = 'iPad';
                // Safari-Version entspricht der iPadOS-Version
                env.osVersion = env.browser === 'Safari' ? env.browserVersion : '?';
            } else {
                env.os = 'macOS'; env.osVersion = m[1].replace(/_/g, '.');
            }
        } else if (/CrOS/.test(ua)) {
            env.os = 'ChromeOS';
        } else if (/Linux/.test(ua)) {
            env.os = 'Linux';
        }

        const uad = navigator.userAgentData;
        if (uad?.getHighEntropyValues) {
            try {
                const h = await uad.getHighEntropyValues(['platformVersion', 'model', 'fullVersionList']);
                if (h.platform) env.os = h.platform;
                if (h.platformVersion) {
                    if (h.platform === 'Windows') {
                        // Windows 11 = platformVersion ab 13
                        const major = parseInt(h.platformVersion, 10);
                        env.osVersion = major >= 13 ? '11' : (major > 0 ? '10' : h.platformVersion);
                    } else {
                        env.osVersion = h.platformVersion;
                    }
                }
                if (h.model) env.device = h.model;
                const brands = (h.fullVersionList || [])
                    .filter((b) => !/Not.?A.?Brand/i.test(b.brand));
                const brand = brands.find((b) => b.brand !== 'Chromium') || brands[0];
                if (brand) {
                    env.browser = brand.brand;
                    env.browserVersion = brand.version;
                }
            } catch (_) {
            }
        }

        env.lang = navigator.language || '?';
        env.langs = (navigator.languages || []).slice(0, 4).join(',');
        const standalone = window.matchMedia?.('(display-mode: standalone)').matches
            || navigator.standalone === true;
        env.mode = standalone ? 'installed' : 'browser';
        env.screen = `${screen.width}x${screen.height}@${window.devicePixelRatio || 1}`;
        try {
            env.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (_) {
        }
        return env;
    }

    // Version = CACHE_VERSION_STRING des Service Workers, der diese Seite
    // steuert. Das ist der Stand, der auf dem Geraet installiert ist -- nicht
    // der auf dem Server. Nur ohne steuernden SW (Erstaufruf, Browser ohne SW)
    // oder wenn ein alter SW nicht antwortet: sw.js frisch lesen wie der
    // System-Info-Dialog (chat-setup.js). Einmal pro Seitenaufruf ermittelt.
    _getClientVersion() {
        if (this._clientVersionPromise) {
            return this._clientVersionPromise;
        }
        const fromServerFile = () => fetch('sw.js?_=' + Date.now())
            .then((r) => r.text())
            .then((txt) => {
                const m = txt.match(/CACHE_VERSION_STRING\s*=\s*"([^"]+)"/);
                return m ? m[1] + ' (sw.js)' : 'unknown';
            })
            .catch(() => 'unknown');

        const controller = navigator.serviceWorker?.controller;
        if (!controller) {
            this._clientVersionPromise = fromServerFile();
            return this._clientVersionPromise;
        }
        this._clientVersionPromise = new Promise((resolve) => {
            const channel = new MessageChannel();
            const timer = setTimeout(() => resolve(null), 1500);
            channel.port1.onmessage = (e) => {
                clearTimeout(timer);
                resolve(e.data?.version || null);
            };
            try {
                controller.postMessage({ type: 'get-version' }, [channel.port2]);
            } catch (_) {
                clearTimeout(timer);
                resolve(null);
            }
        }).then((version) => version || fromServerFile());
        return this._clientVersionPromise;
    }

    // Antwort des Servers auf "client-info" (main-server.js): Ist die laufende
    // PWA aelter als die auf dem Server, Hinweis mit "Aktualisieren" zeigen.
    // Typischer Fall: App lag im Hintergrund-Stack und kommt mit altem Code
    // zurueck -- client-info laeuft bei jedem Reconnect erneut.
    _handleClientVersion(msg) {
        const $banner = $('#pwaUpdateBanner');
        // Zielversion des letzten "Aktualisieren"-Klicks (vor dem Neuladen gesetzt)
        let tried = null;
        try {
            tried = sessionStorage.getItem('pwa-update-tried');
        } catch (_) {
        }
        if (!msg.outdated || !msg.current) {
            $banner.addClass('hidden');
            // Nach dem Neuladen bestaetigt der Server die neue Version -> einmalig
            // kurz bestaetigen und den Merker loeschen.
            if (!msg.outdated && tried && tried === msg.current) {
                VCS.ui?.showToast?.(t('updateDone'), 'success', 3000);
                try {
                    sessionStorage.removeItem('pwa-update-tried');
                } catch (_) {
                }
            }
            return;
        }
        // Schutz gegen Schleife: Fuer diese Zielversion schon neu geladen und
        // trotzdem noch alt (z.B. neuer SW nicht aktiv geworden) -> nicht
        // erneut anbieten. Gilt nur fuer diese Sitzung.
        if (tried === msg.current) {
            console.warn(`[version] Nach Update weiter veraltet, Ziel=${msg.current}`);
            return;
        }
        this._pwaUpdateTarget = msg.current;
        $banner.removeClass('hidden');
        this._prefetchPwaUpdate();
    }

    // Neuen Service Worker schon beim Anzeigen des Hinweises holen. Die
    // Installation laedt die ganze App-Shell (cache.addAll in sw.js) und
    // dauert auf dem Handy einige Sekunden -- bis der Nutzer tippt, ist sie
    // meist fertig. controllerchange merken, damit der Klick dann sofort
    // neu laden kann (das Ereignis kommt nur einmal).
    _prefetchPwaUpdate() {
        const sw = navigator.serviceWorker;
        if (!sw || this._pwaUpdatePrefetched) {
            return;
        }
        this._pwaUpdatePrefetched = true;
        this._pwaSwSwitched = false;
        sw.addEventListener('controllerchange', () => {
            this._pwaSwSwitched = true;
        }, { once: true });
        sw.getRegistration()
            .then((reg) => reg?.update())
            .catch((err) => console.warn('[version] SW-Update im Hintergrund fehlgeschlagen:', err));
    }

    // Neu laden, sobald der neue Service Worker uebernommen hat (skipWaiting +
    // clients.claim in sw.js -> controllerchange). Hat er schon uebernommen,
    // sofort. Sonst auf controllerchange warten, hoechstens 8 s -- danach
    // trotzdem neu laden: index.php und main.min.js holt sw.js zuerst vom Netz.
    _applyPwaUpdate() {
        try {
            sessionStorage.setItem('pwa-update-tried', this._pwaUpdateTarget || '');
        } catch (_) {
        }
        $('#pwaUpdateBtn').prop('disabled', true);
        let done = false;
        const reload = () => {
            if (done) {
                return;
            }
            done = true;
            // Chrome/Android stellt sonst eine verschobene Ansicht wieder her
            // (untere Navigation unter dem Rand)
            try {
                history.scrollRestoration = 'manual';
                window.scrollTo(0, 0);
            } catch (_) {
            }
            location.reload();
        };
        const sw = navigator.serviceWorker;
        if (!sw || this._pwaSwSwitched) {
            reload();
            return;
        }
        sw.addEventListener('controllerchange', reload, { once: true });
        setTimeout(reload, 8000);
        // Hat der neue SW schon vor dem Hinweis uebernommen, kam controllerchange
        // zu frueh fuer den Listener -- dann meldet der steuernde SW bereits
        // die Zielversion.
        this._askControllerVersion().then((version) => {
            if (version && version === this._pwaUpdateTarget) {
                reload();
            }
        });
        if (!this._pwaUpdatePrefetched) {
            sw.getRegistration()
                .then((reg) => (reg ? reg.update() : reload()))
                .catch(reload);
        }
    }

    // Version des gerade steuernden Service Workers (sw.js: "get-version").
    // Anders als _getClientVersion() nicht pro Seitenaufruf gemerkt.
    _askControllerVersion() {
        const controller = navigator.serviceWorker?.controller;
        if (!controller) {
            return Promise.resolve(null);
        }
        return new Promise((resolve) => {
            const channel = new MessageChannel();
            const timer = setTimeout(() => resolve(null), 1000);
            channel.port1.onmessage = (e) => {
                clearTimeout(timer);
                resolve(e.data?.version || null);
            };
            try {
                controller.postMessage({ type: 'get-version' }, [channel.port2]);
            } catch (_) {
                clearTimeout(timer);
                resolve(null);
            }
        });
    }

    // Binärdaten an Server senden (z.B. PCM-Audio für Deepgram)
    sendBinary(buffer) {
        if (VCS.state.ws && VCS.state.ws.readyState === WebSocket.OPEN) {
            VCS.state.ws.send(buffer);
        }
    }

    // Reconnect mit exponentiellem Backoff
    scheduleReconnect(delayOverride = null) {
        const s = VCS.state;
        if (s.reconnectTimer) {
            return;
        }
        const delays = [1000, 2000, 5000, 8000];
        const delay = delayOverride ?? delays[Math.min(s.reconnectAttempts, delays.length - 1)];
        if (delayOverride === null) {
            s.reconnectAttempts += 1;
        }

        this._updateStatusBadge(t('reconnecting'), 'prejoin-status--warning');

        s.reconnectTimer = setTimeout(() => {
            s.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    // Registrierung beim Server anfragen
    requestRegister() {
        const s = VCS.state;
        if (!s.myName) {
            return;
        }
        s.pendingRegisterOnOpen = true;

        if (s.ws && s.ws.readyState === WebSocket.OPEN) {
            this.send(this._buildRegisterMessage());
            VCS.captions?.sendConfig();
            s.pendingRegisterOnOpen = false;
            return;
        }

        this.connect();
    }

    // "register" fuer den Raum. guestAccept: der Einladende hat einen
    // Gast-Anruf im Klingel-Dialog angenommen -- der Server laesst den Gast
    // dann ohne zweites Nachfragen herein (main-server.js, case "register").
    _buildRegisterMessage() {
        const s = VCS.state;
        const msg = {
            type  : 'register',
            id    : s.myId,
            name  : s.myName,
            room  : s.ROOM_ID,
            camOff: s.videoOff,
            lang  : VCS.i18n?.normalizeLang(s.currentLang) || s.currentLang,
        };
        if (s.directCallActive) {
            msg.direct = true;
        }
        if (s.guestAcceptPending) {
            msg.guestAccept = true;
        }
        return msg;
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Presence: App-Hintergrund/Vordergrund erkennen und die WS aktiv
        // trennen bzw. wieder aufbauen. Ohne das meldet sich die PWA erst nach
        // dem Server-seitigen Idle-Timeout (PRESENCE_IDLE_TIMEOUT_MS, bis zu
        // 10 Minuten) ab -- Chat-Partner sehen den Nutzer solange faelschlich
        // als "online". Ein sauberes ws.close() feuert dagegen sofort den
        // Offline-Broadcast des Servers.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this._handleAppHidden();
            } else if (document.visibilityState === 'visible') {
                this._handleAppVisible();
            }
        });

        // pagehide/pageshow: zuverlaessiger als visibilitychange beim
        // Schliessen/Wegnavigieren und beim iOS-Safari-Freeze (bfcache).
        window.addEventListener('pagehide', () => this._handleAppHidden());
        window.addEventListener('pageshow', () => this._handleAppVisible());

        $(document).on('click', '#pwaUpdateBtn', () => this._applyPwaUpdate());
    }

    // ── Private Methoden ─────────────────────────────────────

    // Aktiver Call/Waiting-Zustand -- dann die WS NIE trennen, sonst bricht
    // der laufende Anruf ab. Der Server-Idle-Reaper nimmt In-Call-Sockets
    // ebenfalls aus.
    _isInCall() {
        const s = VCS.state;
        return s.sessionState?.phase === 'call'
            || s.sessionState?.phase === 'waiting'
            || s.groupCallActive === true;
    }

    // App wandert in den Hintergrund / wird geschlossen.
    _handleAppHidden() {
        const s = VCS.state;
        // Waehrend eines Calls nichts tun -- Verbindung muss stehen bleiben.
        if (this._isInCall()) {
            return;
        }
        // Ohne Chat-Login gibt es keine Presence zu melden.
        if (!s.chatMyPhone) {
            return;
        }
        s.presenceHidden = true;
        // Laufenden Reconnect-Versuch stoppen -- im Hintergrund wollen wir
        // gerade NICHT verbunden sein.
        if (s.reconnectTimer) {
            clearTimeout(s.reconnectTimer);
            s.reconnectTimer = null;
        }
        // WS sauber schliessen -> Server ws.on('close') -> Offline-Broadcast
        // an alle Chat-Partner in Echtzeit.
        if (s.ws && (s.ws.readyState === WebSocket.OPEN || s.ws.readyState === WebSocket.CONNECTING)) {
            try {
                s.ws.close(1000, 'app_hidden');
            } catch (_) {
                // close() kann im Freeze-Zustand werfen -- ignorieren.
            }
        }
    }

    // App kommt zurueck in den Vordergrund.
    _handleAppVisible() {
        const s = VCS.state;
        // Nur reagieren, wenn WIR die WS wegen Hintergrund getrennt hatten.
        if (!s.presenceHidden) {
            return;
        }
        s.presenceHidden = false;
        // connect() sendet in onopen automatisch chat-auth erneut -> Server
        // broadcastet wieder 'online' an die Chat-Partner.
        this.connect();
    }

    // Status-Badge aktualisieren (Pre-Join + Tab-Call)
    _updateStatusBadge(text, cls) {
        this._view.preStatus.add(this._view.tabCallStatus).each(function () {
            this.textContent = text;
            this.className = 'prejoin-status-badge ' + cls;
        });
    }

    // Server-Nachrichten verarbeiten
    async _handleMessage(msg) {
        const s = VCS.state;

        // Dolmetscher mit Partner-Geraet: eigener Namensraum (interpret-room.js).
        if (typeof msg.type === 'string' && msg.type.startsWith('interpret-')) {
            VCS.interpretRoom?.onMessage?.(msg);
            return;
        }

        switch (msg.type) {

            // ── Registrierung erfolgreich ────────────────────
            case 'registered':
                s.myRole = msg.role;
                VCS.session?.update({
                                        name : s.myName,
                                        role : s.myRole,
                                        phase: 'waiting',
                                    });
                VCS.nav?.showScreen('waitingScreen');
                VCS.session?.setRejoinHint(false);

                if (msg.role === 'host') {
                    this._view.hostWaiting.removeClass('hidden');
                    this._view.guestWaiting.addClass('hidden');
                } else {
                    this._view.hostWaiting.addClass('hidden');
                    this._view.guestWaiting.removeClass('hidden');
                }
                // Gespraechszusammenfassung: Der Wunsch wurde vor dem Anruf
                // abgefragt, abgeschickt wird er erst hier -- vorher gibt es
                // serverseitig keinen Raum, an dem die Mitschrift haengen koennte.
                // Nach einem Reconnect mitten im Gespraech laeuft derselbe Weg
                // noch einmal; der Server nimmt den Besteller dann wieder auf.
                if (s.callSummaryWish) {
                    this.send({ type: 'call-summary-start' });
                }
                // Gast-Anruf: guestAccept gilt nur fuer genau dieses register.
                s.guestAcceptPending = false;
                break;

            // -- Gast-Anruf, Gast-Seite (guest-app.js) --
            // Anmeldung mit dem Gast-Schluessel bestaetigt: jetzt darf register folgen.
            case 'guest-authed':
                VCS.guestApp?.onAuthed?.(msg);
                break;

            case 'guest-auth-denied':
                VCS.guestApp?.onAuthDenied?.(msg);
                break;

            // Der Einladende hat innerhalb der Klingelzeit nicht reagiert.
            case 'guest-no-answer':
                VCS.guestApp?.onNoAnswer?.(msg);
                break;

            // -- Gast-Anruf, Seite des Einladenden (guest-invite.js) --
            case 'guest-knock':
                VCS.guestInvite?.onKnock?.(msg);
                break;

            case 'guest-knock-cancelled':
                VCS.guestInvite?.onKnockCancelled?.(msg);
                break;

            // -- Gespraechszusammenfassung: Antwort des Servers --
            // Kommt auf 'call-summary-start' und auf 'call-summary-cancel'.
            // active=false heisst: kein Recht, oder der Nutzer hat abbestellt.
            case 'call-summary-state':
                s.callSummaryActive = !!msg.active;
                if (!msg.active) {
                    s.callSummaryWish = false;
                }
                VCS.callControls?.updateSummaryDot?.();
                // Gruppen-Call: der gelbe Punkt sitzt an der eigenen Kachel.
                if (s.groupCallActive) VCS.webrtc?.renderGroupGrid();
                break;

            // -- Mitschrift-Erkennung fuer die Zusammenfassung (03.10.) --
            // 1:1, gleiche Sprache: der Server sagt, wann aufgenommen werden
            // muss -- unabhaengig vom CC-Schalter, der nur die Anzeige steuert.
            case 'summary-capture':
                VCS.captions?.setSummaryCapture?.(msg.on === true);
                break;

            // -- Gespraechszusammenfassung: das GEGENUEBER laesst zusammenfassen --
            // (call-summary.js notifyRoom, seit 27.09.). Eigenes Symbol am Namen,
            // getrennt vom gelben Punkt der eigenen Bestellung. Im Gruppen-Call
            // (28.09.) nennt `orderers` die Kacheln, an die das Symbol gehoert.
            case 'call-summary-peer':
                s.callSummaryPeerActive = !!msg.active;
                s.callSummaryPeerOrderers = Array.isArray(msg.orderers) ? msg.orderers : [];
                VCS.callControls?.updateSummaryDot?.();
                if (s.groupCallActive) VCS.webrtc?.renderGroupGrid();
                break;

            // Admin hat das Recht geaendert -- greift ab dem naechsten Anruf,
            // ohne dass der Nutzer die App neu laden muss.
            case 'call-summary-updated':
                s.callSummaryAvailable = !!msg.enabled;
                break;

            // ── Host wartet ──────────────────────────────────
            case 'waiting':
                break;

            // ── Beitrittsanfrage (an Host) ───────────────────
            case 'join-request':
                s.pendingGuestId = msg.guestId;
                s.pendingGuestCamOff = msg.guestCamOff === true;
                VCS.callControls?.updateApprovalCallType();
                this._view.approvalName.text((msg.guestName || '').trim() || t('guest'));
                this._view.approvalOverlay.removeClass('hidden');
                VCS.audioMonitor?.startIncomingKnock();
                VCS.notify?.maybeNotifyJoinRequest(msg.guestName || t('guest'));
                break;

            // ── Anfrage zurückgezogen ────────────────────────
            case 'request-cancelled':
                this._view.approvalOverlay.addClass('hidden');
                s.pendingGuestId = null;
                s.pendingGuestCamOff = false;
                VCS.callControls?.updateApprovalCallType();
                VCS.audioMonitor?.stopIncomingKnock();
                s.activeJoinNotification?.close();
                s.activeJoinNotification = null;
                VCS.ui?.showToast(t('requestCancelled'), 'info');
                break;

            // ── Guest wurde genehmigt ────────────────────────
            case 'approved':
                s.peerName = msg.hostName;
                s.peerLang = msg.hostLang ? VCS.i18n?.normalizeLang(msg.hostLang) : s.peerLang;
                VCS.audioMonitor?.stopIncomingKnock();
                s.activeJoinNotification?.close();
                s.activeJoinNotification = null;
                VCS.session?.update({
                                        name : s.myName,
                                        phase: 'call',
                                    });
                VCS.session?.setRejoinHint(false);
                await VCS.webrtc?.startCall(true); // Guest erstellt Offer
                // Phase ist jetzt 'call' -- kam summary-capture schon vorher,
                // die Aufnahme jetzt nachholen.
                VCS.captions?.resumeSummaryCapture?.();
                break;

            // ── Host: Call starten (nach Genehmigung) ────────
            case 'start-call': {
                s.peerName = msg.guestName;
                s.peerLang = msg.guestLang ? VCS.i18n?.normalizeLang(msg.guestLang) : s.peerLang;
                const startInPhoneMode = msg.guestCamOff === true;
                if (startInPhoneMode) {
                    s.videoOff = true;
                    s.camOffByButton = false;
                    VCS.media?._clearCamHardStopTimer();
                    s.baseStream?.getVideoTracks().forEach(t => t.enabled = false);
                    s.localStream?.getVideoTracks().forEach(t => t.enabled = false);
                }
                VCS.audioMonitor?.stopIncomingKnock();
                s.activeJoinNotification?.close();
                s.activeJoinNotification = null;
                VCS.session?.update({
                                        name : s.myName,
                                        phase: 'call',
                                    });
                VCS.session?.setRejoinHint(false);
                await VCS.webrtc?.startCall(false); // Host wartet auf Offer
                VCS.captions?.resumeSummaryCapture?.();
                this._view.remoteCamOff.toggleClass('hidden', !startInPhoneMode);
                if (startInPhoneMode) {
                    this.send({
                                  type  : 'cam',
                                  id    : s.myId,
                                  camOff: true,
                              });
                }
                break;
            }

            // ── Abgelehnt ───────────────────────────────────
            case 'denied':
                this._view.deniedOverlay.removeClass('hidden');
                s.myRole = null;
                VCS.session?.resetToPreJoin(true);
                VCS.session?.setRejoinHint(false);
                break;

            // ── Direct-Call abgelehnt ──────────────────────
            case 'call-declined':
                VCS.ui?.showToast(t('callDeclined'), 'info', 4000);
                VCS.nav?.showHomeScreen();
                s.myRole = null;
                s.directCallActive = false;
                s.directCallPeerName = '';
                s.directCallPeerPhone = '';
                VCS.session?.resetToPreJoin(true);
                VCS.session?.setRejoinHint(false);
                break;

            // ── Direct-Call keine Antwort (Timeout) ───────
            case 'call-no-answer':
                VCS.ui?.showToast(t('callNoAnswer'), 'info', 4000);
                VCS.nav?.showHomeScreen();
                s.myRole = null;
                s.directCallActive = false;
                s.directCallPeerName = '';
                s.directCallPeerPhone = '';
                VCS.session?.resetToPreJoin(true);
                VCS.session?.setRejoinHint(false);
                break;

            // ── Direct-Call vom Caller abgebrochen (Callee-Seite) ──
            case 'call-cancelled':
                document.querySelector('.incoming-call-overlay')?.remove();
                VCS.ui?.showToast(t('callCancelled'), 'info', 3000);
                break;

            // ── Anderes Geraet desselben Users hat angenommen/abgelehnt ──
            case 'call-handled-elsewhere':
                document.querySelector('.incoming-call-overlay')?.remove();
                // Service-Worker-Notification (falls vorhanden) schliessen
                if ('serviceWorker' in navigator && msg.callId) {
                    navigator.serviceWorker.ready.then(reg => {
                        reg.getNotifications({ tag: 'call-' + msg.callId }).then(list => {
                            list.forEach(n => n.close());
                        });
                    }).catch(() => {});
                }
                break;

            // ── Raum voll ───────────────────────────────────
            case 'room-full':
                VCS.ui?.showToast(t('roomFull'), 'error');
                VCS.nav?.showHomeScreen();
                s.myRole = null;
                VCS.session?.resetToPreJoin(true);
                VCS.session?.setRejoinHint(false);
                break;

            // ── Guest zum Host befördert ─────────────────────
            case 'promoted-to-host':
                s.myRole = 'host';
                VCS.session?.update({
                                        name : s.myName,
                                        role : s.myRole,
                                        phase: 'waiting',
                                    });
                VCS.nav?.showScreen('waitingScreen');
                this._view.hostWaiting.removeClass('hidden');
                this._view.guestWaiting.addClass('hidden');
                VCS.ui?.showToast(t('youAreHost'), 'info');
                break;

            // ── WebRTC Offer ─────────────────────────────────
            case 'offer':
                await VCS.webrtc?.handleOffer(msg);
                break;

            // ── WebRTC Answer ────────────────────────────────
            case 'answer':
                if (s.pc) {
                    await s.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
                    VCS.webrtc?.flushCandidates();
                }
                break;

            // ── ICE Candidate ────────────────────────────────
            case 'candidate':
                if (s.pc && s.pc.remoteDescription) {
                    await s.pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
                } else {
                    s.candidateQueue.push(msg.candidate);
                }
                break;

            // ── Gesprächspartner hat verlassen ───────────────
            case 'peer-left':
                VCS.webrtc?.endCall(t('partnerLeft'));
                break;

            // ── SFU-Fallback fuer 1:1-Calls ────────────────
            case 'sfu-fallback-start':
                console.log('[signaling] SFU-Fallback empfangen: sfuRoom=' + msg.sfuRoomId);
                VCS.webrtc?.switchToSfu(msg.sfuRoomId);
                break;

            // ── Group Video Call ────────────────────────────

            case 'group-call-joined': {
                // Server bestaetigt Join — Teilnehmer vorregistrieren
                for (const peer of (msg.participants || [])) {
                    if (!s.groupCallPeers.has(peer.id)) {
                        s.groupCallPeers.set(peer.id, {
                            remoteStream: null,
                            name        : peer.name,
                            camOff      : peer.camOff,
                            muted       : peer.muted,
                        });
                    }
                }
                VCS.webrtc?.renderGroupGrid();

                // Mesh-PeerConnections fuer alle bestehenden Teilnehmer starten
                for (const [peerId, peer] of s.groupCallPeers) {
                    VCS.webrtc?._createGroupPC(peerId, peer.name, peer.camOff, true)
                        .catch(e => console.error('[webrtc] createGroupPC error:', e));
                }

                // Captions
                VCS.captions?.sendConfig();
                VCS.captions?.startAutoCapture().catch(() => {});

                // Gespraechszusammenfassung: gefragt wurde vor dem Beitreten
                // (chat-groups.js _askGroupSummary), abgeschickt erst hier --
                // vorher kennt der Server keinen Raum fuer die Mitschrift.
                if (s.callSummaryWish) {
                    this.send({ type: 'call-summary-start' });
                }
                break;
            }

            case 'group-peer-joined':
                if (!s.groupCallPeers.has(msg.peerId)) {
                    s.groupCallPeers.set(msg.peerId, {
                        remoteStream: null,
                        name        : msg.peerName,
                        camOff      : msg.peerCamOff,
                        muted       : false,
                    });
                }
                // Kein _createGroupPC hier! Der neue Peer sendet den Offer
                // und handleGroupOffer erstellt die PC automatisch.
                VCS.webrtc?.renderGroupGrid();
                VCS.ui?.showToast(msg.peerName + ' ' + t('groupPeerJoined'), 'info');
                break;

            // Mesh-Signaling (P2P zwischen Peers)
            case 'group-offer':
                VCS.webrtc?.handleGroupOffer(msg.from, msg.sdp);
                break;

            case 'group-answer':
                VCS.webrtc?.handleGroupAnswer(msg.from, msg.sdp);
                break;

            case 'group-candidate':
                VCS.webrtc?.handleGroupCandidate(msg.from, msg.candidate);
                break;

            // SFU-Fallback fuer Group-Calls (Server hat alle Peers umgehaengt)
            case 'sfu-group-fallback-start':
                VCS.webrtc?.switchGroupToSfu(msg.sfuRoomId);
                break;

            // ── SFU-Responses (mediasoup) ────────────────────
            case 'sfu-capabilities':
            case 'sfu-transport-created':
            case 'sfu-produced':
            case 'sfu-consumed':
            case 'sfu-existing-producers':
                s._sfuClient?.handleResponse(msg);
                break;

            case 'sfu-transport-connected':
                s._sfuClient?.handleTransportConnected(msg);
                break;

            // Neuer Producer eines Peers → konsumieren
            case 'sfu-new-producer':
                if (s._sfuClient && msg.clientId !== s.myId) {
                    s._sfuClient.consumeProducer(msg.producerId, msg.clientId, msg.kind)
                        .catch(e => console.error('[sfu] consume new-producer error:', e));
                }
                break;

            // Producer eines Peers geschlossen (Cam off / Disconnect)
            case 'sfu-producer-closed':
                if (msg.consumerId) {
                    s._sfuClient?.removeConsumerByProducerClose(msg.consumerId);
                }
                break;

            case 'sfu-error':
                console.error('[sfu] Server-Fehler:', msg.message);
                break;

            case 'group-mute':
                VCS.webrtc?.updateGroupPeerMute(msg.from, msg.muted);
                break;

            case 'group-cam':
                VCS.webrtc?.updateGroupPeerCam(msg.from, msg.camOff);
                break;

            case 'group-peer-left':
                VCS.webrtc?.removeGroupPeer(msg.peerId);
                break;

            case 'incoming-group-call':
                // Ruhemodus: nicht klingeln. Das Banner "Gruppenanruf laeuft"
                // (group-call-active) bleibt, damit man selbst beitreten kann.
                if (VCS.notify?.isQuiet?.()) {
                    console.log('[quiet] Gruppenanruf ignoriert: ' + msg.callId);
                    break;
                }
                VCS.chatGroups?.showIncomingGroupCallToast(msg);
                break;

            case 'group-call-active':
                VCS.chatGroups?.showGroupCallBanner(msg.callId, msg.groupId);
                break;

            case 'group-call-ended':
                VCS.chatGroups?.hideGroupCallBanner(msg.groupId);
                break;

            // ── Remote Mute-Status ──────────────────────────
            case 'mute':
                s.remoteMuted = !!msg.muted;
                this._view.remoteMuteIndicator.toggleClass('hidden', !msg.muted);
                if (s.remoteMuted) {
                    VCS.audioMonitor?.setRemoteCamOffSpeaking(false);
                }
                break;

            // ── Remote Kamera-Status ────────────────────────
            case 'cam':
                this._view.remoteCamOff.toggleClass('hidden', !msg.camOff);
                if (!msg.camOff) {
                    VCS.audioMonitor?.setRemoteCamOffSpeaking(false);
                }
                break;

            // ── Captions ─────────────────────────────────────
            case 'caption-translated':
                if ((s.captionsEnabled || s.captionOnlyMode) && typeof msg.text === 'string') {
                    // phase: 'interim' | 'final' (Default 'final' fuer
                    // Backward-Compat alter Server-Builds ohne Interim-Modus).
                    const phase = msg.phase === 'interim' ? 'interim' : 'final';
                    // Group-Mode: senderId (msg.from) wird in captions.js zum Routing
                    // an das richtige Sprecher-Tile genutzt. 1:1: wird ignoriert.
                    VCS.captions?.showTranslated(msg.text, msg.senderName, msg.partial, phase, msg.from);
                }
                break;

            case 'caption-transcript':
                if ((s.captionsEnabled || s.captionOnlyMode) && typeof msg.text === 'string') {
                    VCS.captions?.showSelfTranscript(msg.text, msg.partial);
                }
                break;

            // Audio-Toggle (openai-realtime engine only): translated audio
            // chunk for playback. The AudioFader keeps a graph ready and
            // respects VCS.state.audioFaderMode ('original' = silent,
            // 'translated' = full volume). speakerOff mutes both paths.
            case 'caption-audio-chunk':
                if (s.captionsEnabled || s.captionOnlyMode) {
                    VCS.audioFader?.handleChunk(msg);
                }
                break;

            // Audio-Fader engine + audio-availability info, pushed by
            // backend on caption-stream init (and on close to clear the
            // flag). Drives slider visibility on the receiver side.
            case 'caption-engine-info':
                VCS.audioFader?.onEngineInfo(msg);
                break;

            // ── Live-Unterhaltung (Solo-Dolmetscher) ─────────
            case 'live-convo-started':
                VCS.liveConvo?.onStarted(msg);
                break;
            case 'live-convo-transcript':
                VCS.liveConvo?.onTranscript(msg);
                break;
            case 'live-convo-translated':
                VCS.liveConvo?.onTranslated(msg);
                break;
            case 'live-convo-audio-chunk':
                VCS.liveConvo?.onAudioChunk(msg);
                break;
            case 'live-convo-switched':
                VCS.liveConvo?.onSwitched(msg);
                break;
            case 'live-convo-stopped':
                VCS.liveConvo?.onStopped(msg);
                break;
            case 'live-convo-error':
                VCS.liveConvo?.onError(msg);
                break;

            // Admin hat das Recht geaendert -- der Menuepunkt erscheint bzw.
            // verschwindet sofort, ohne dass der Nutzer die App neu laedt.
            // Laeuft gerade eine Sitzung, beendet sie der Entzug nicht: der
            // Server hat sie regulaer eroeffnet, und ein Abbruch mitten im Satz
            // waere fuer den Nutzer nicht erklaerbar. Der naechste Start wird
            // abgelehnt.
            case 'live-convo-updated':
                s.liveConvoAvailable = !!msg.enabled;
                // Partner-Einladen haengt am selben Recht. Der Not-Aus des Servers
                // greift trotzdem -- er lehnt dann beim Erzeugen des Links ab.
                s.liveConvoRemote = !!msg.enabled;
                VCS.interpretRoom?.renderInviteButton?.();
                break;

            // Admin hat das Concierge-Recht geaendert -- der Bot-Chat erscheint
            // bzw. verschwindet sofort in der Liste.
            case 'concierge-updated':
                s.conciergeAvailable = !!msg.enabled;
                VCS.chatSetup?._ensureBotChat?.();
                VCS.chatMessages?.renderChatList?.();
                break;

            case 'caption-only-activated':
                s.captionOnlyMode = true;
                s.captionsEnabled = true;
                VCS.callControls?._view?.btnCaptionOnly?.toggleClass('btn-active-green', true);
                VCS.captions?.sendConfig();
                VCS.captions?.startAutoCapture().catch(() => {
                });
                VCS.captions?.updateFabUI();
                VCS.ui?.showToast?.(t('captionOnlyOn'), 'success', 1800);
                break;

            case 'caption-only-deactivated':
                s.captionOnlyMode = false;
                VCS.callControls?._view?.btnCaptionOnly?.toggleClass('btn-active-green', false);
                // Laeuft die Mitschrift-Erkennung, endet nur die Anzeige.
                if (s.summaryCapture) {
                    VCS.captions?.stopDisplayKeepCapture?.();
                } else {
                    VCS.captions?.releasePipeline();
                }
                VCS.captions?.clearTranslated();
                VCS.captions?.clearSelfTranscript();
                VCS.captions?.updateFabUI();
                VCS.ui?.showToast?.(t('captionOnlyOff'), 'info', 1800);
                break;

            case 'caption-peer-lang':
                s.peerLang = VCS.i18n?.normalizeLang(msg.peerLang);
                // Mitschrift-Erkennung (summary-capture) braucht die Pipeline
                // auch bei gleicher Sprache ohne CC.
                if (!s.captionOnlyMode && !s.summaryCapture &&
                    (!s.peerLang ||
                        s.peerLang === VCS.i18n?.normalizeLang(s.currentLang) ||
                        !VCS.i18n?.isTranslationSupported(s.peerLang) ||
                        !VCS.i18n?.isTranslationSupported(VCS.i18n?.normalizeLang(s.currentLang)))) {
                    VCS.captions?.releasePipeline();
                } else if ((s.captionsEnabled || s.captionOnlyMode) && s.sessionState.phase === 'call' && !s.captionPaused) {
                    VCS.captions?.startAutoCapture().catch(() => {
                    });
                }
                VCS.captions?.updateFabUI();
                break;

            // ── TURN Credentials ─────────────────────────────
            case 'turn-credentials':
                if (msg.iceServers && msg.iceServers.length) {
                    VCS.Signaling.ICE_SERVERS = msg.iceServers;
                }
                break;

            // ── Chat Events ──────────────────────────────────

            case 'chat-auth-denied':
                if (msg.reason === 'invalid_token') {
                    console.warn('[signaling] Token ungültig, lösche altes Token');
                    s.chatAuthToken = null;
                    localStorage.removeItem('chat-auth-token');
                    // Neu-Auth ohne Token versuchen (Server generiert neues)
                    if (s.chatMyPhone) {
                        this.send({ type: 'chat-auth', phone: s.chatMyPhone });
                    }
                } else if (msg.reason === 'account_inactive' || msg.reason === 'account_deleted') {
                    void VCS.chatSetup?.forceLogout();
                }
                break;

            case 'client-version':
                this._handleClientVersion(msg);
                break;

            case 'chat-authed':
                this._sendClientInfo();
                this.sendActiveChat();
                if (msg.token) {
                    s.chatAuthToken = msg.token;
                    localStorage.setItem('chat-auth-token', msg.token);
                }
                if (msg.plan) {
                    s.chatMyPlan = msg.plan;
                    localStorage.setItem('chat-plan', s.chatMyPlan);
                }
                // Plan-Eigenschaften vom Server (siehe state.js). Nur uebernehmen,
                // wenn der Server sie mitschickt -- laeuft noch eine aeltere
                // Backend-Version, bleiben die gespeicherten Werte unangetastet.
                if (msg.planPaid !== undefined) {
                    s.chatPlanPaid = !!msg.planPaid;
                    localStorage.setItem('chat-plan-paid', s.chatPlanPaid ? '1' : '0');
                }
                if (msg.planLiveQuota !== undefined) {
                    s.chatPlanLiveQuota = !!msg.planLiveQuota;
                    localStorage.setItem('chat-plan-live-quota', s.chatPlanLiveQuota ? '1' : '0');
                }
                // Kaufrecht Minutenpakete (2026-08-31): getrennt von chatPlanPaid, sonst
                // bleibt die Kauf-Karte fuer ein echtes Free-Konto ohne Restguthaben
                // dauerhaft unsichtbar, auch wenn MINUTES_FREE_PURCHASE=1 steht.
                if (msg.canBuyMinutes !== undefined) {
                    s.chatCanBuyMinutes = !!msg.canBuyMinutes;
                    localStorage.setItem('chat-can-buy-minutes', s.chatCanBuyMinutes ? '1' : '0');
                }
                if (msg.planDisplayName) {
                    s.chatPlanDisplayName = msg.planDisplayName;
                    localStorage.setItem('chat-plan-name', s.chatPlanDisplayName);
                }
                if (msg.planDisplayName) {
                    s.chatPlanDisplayName = msg.planDisplayName;
                    localStorage.setItem('chat-plan-name', s.chatPlanDisplayName);
                }
                if (msg.trialEndsAt !== undefined) {
                    s.chatTrialEndsAt = msg.trialEndsAt || 0;
                    localStorage.setItem('chat-trial-ends-at', String(s.chatTrialEndsAt));
                    VCS.chatSetup?.updateSettingsUI?.();
                }
                if (msg.proTrialSecondsTotal !== undefined) {
                    s.chatProTrialSecondsUsed = msg.proTrialSecondsUsed || 0;
                    s.chatProTrialSecondsTotal = msg.proTrialSecondsTotal;
                    localStorage.setItem('chat-pro-trial-used', String(s.chatProTrialSecondsUsed));
                    localStorage.setItem('chat-pro-trial-total', String(s.chatProTrialSecondsTotal));
                }
                // Live-Cap initial values: backend sends these on every chat-authed so the
                // Settings block (#settingsLiveCap) is visible immediately at login,
                // not only after the first 'live-cap-update' push from a caption tick.
                if (msg.liveCapSecondsTotal !== undefined) {
                    s.chatLiveCapSecondsUsedMonth  = msg.liveCapSecondsUsedMonth  || 0;
                    s.chatLiveCapSecondsTotal      = msg.liveCapSecondsTotal      || 0;
                    s.chatLiveCapGroupSecondsUsed  = msg.liveCapGroupSecondsUsed  || 0;
                    s.chatLiveCapGroupSecondsTotal = msg.liveCapGroupSecondsTotal || 0;
                    s.chatLiveCapPeriodStart       = msg.liveCapPeriodStart       || 0;
                    s.chatLiveCapUnlimited         = !!msg.liveCapUnlimited;
                    if (msg.liveExtraSeconds !== undefined) {
                        s.chatLiveExtraSeconds = msg.liveExtraSeconds || 0;
                    }
                    try {
                        localStorage.setItem('chat-live-cap-used',         String(s.chatLiveCapSecondsUsedMonth));
                        localStorage.setItem('chat-live-cap-total',        String(s.chatLiveCapSecondsTotal));
                        localStorage.setItem('chat-live-cap-group-used',   String(s.chatLiveCapGroupSecondsUsed));
                        localStorage.setItem('chat-live-cap-group-total',  String(s.chatLiveCapGroupSecondsTotal));
                        localStorage.setItem('chat-live-cap-period-start', String(s.chatLiveCapPeriodStart));
                        localStorage.setItem('chat-live-cap-unlimited',    s.chatLiveCapUnlimited ? '1' : '0');
                        localStorage.setItem('chat-live-extra',            String(s.chatLiveExtraSeconds));
                    } catch (_) {}
                    VCS.chatSetup?.updateSettingsUI?.();
                }
                if (msg.avatarHash !== undefined) {
                    s.chatAvatarHash = msg.avatarHash || null;
                    if (msg.avatarHash) {
                        localStorage.setItem('chat-avatar-hash', msg.avatarHash);
                    } else {
                        localStorage.removeItem('chat-avatar-hash');
                    }
                    VCS.chatSetup?.updateProfileUI?.();
                }
                // Multi-Device Sync: Name + Sprache vom Server uebernehmen
                if (msg.displayName && msg.displayName !== s.chatMyName) {
                    s.chatMyName = msg.displayName;
                    try { localStorage.setItem('chat-name', msg.displayName); } catch (_) {}
                    const nameInput = $('#profileNameInput')[0];
                    if (nameInput) nameInput.value = msg.displayName;
                    const regInput = $('#regNameInput')[0];
                    if (regInput) regInput.value = msg.displayName;
                    VCS.chatSetup?.updateProfileUI?.();
                    console.log('[signaling] chat-authed: name synced to', msg.displayName);
                }
                if (msg.lang && msg.lang !== s.currentLang) {
                    VCS.i18n?.setLang(msg.lang);
                    console.log('[signaling] chat-authed: lang synced to', msg.lang);
                }
                // Gepufferte Push-Debug-Meldungen nachsenden
                VCS.state._pushDbgFlush?.();
                // Dating: kein Plan-Feature, eigener Ein/Aus-Schalter (global UND pro
                // User, siehe main-server.js chat-auth). Bewusst KEIN localStorage-Cache
                // -- ist der Server nicht erreichbar, bleibt das Menue versteckt statt
                // den letzten bekannten Stand zu zeigen.
                VCS.chatSetup?.applyDatingEnabled?.(!!msg.datingEnabled);
                // Gespraechszusammenfassung: Das Recht kommt ausschliesslich vom
                // Server -- er prueft Freigabe, globalen Schalter UND hinterlegte
                // Mailadresse. Wie beim Dating kein localStorage-Cache: ohne
                // Serverantwort erscheint der Dialog gar nicht (fail-closed).
                s.callSummaryAvailable = !!msg.callSummaryEnabled;
                VCS.chatContacts?.refreshIncomingCallOptIn?.();
                // Live-Unterhaltung: eigenes Recht, kein Plan-Feature. Ebenfalls
                // ohne localStorage-Cache -- ohne Serverantwort fehlt der
                // Menuepunkt (fail-closed). Der Server lehnt den Start zusaetzlich
                // ab, die versteckte Zeile allein waere kein Schutz.
                s.liveConvoAvailable = !!msg.liveConvoEnabled;
                // Dolmetscher mit Partner-Geraet (2026-10-16): Knopf "Partner
                // einladen". Laeuft eine Sitzung, betritt der Client den Raum neu.
                s.liveConvoRemote = !!msg.liveConvoRemote;
                VCS.interpretRoom?.onChatAuthed?.();
                // Concierge (2026-10-15): eigenes Recht, unabhaengig vom Tarif.
                // Fail-closed wie oben -- ohne Serverantwort kein Bot-Chat. Die
                // Liste gleich nachziehen: loadChats kann schon gelaufen sein.
                s.conciergeAvailable = !!msg.conciergeEnabled;
                VCS.chatSetup?._ensureBotChat?.();
                VCS.chatMessages?.renderChatList?.();
                // Inhalts-Stempel (2026-10-26): nur nachladen, was sich geaendert hat.
                VCS.chatMessages?.onServerVersions?.(msg.versions);
                // Presence events were missed while disconnected, so refresh after every reconnect.
                VCS.chatPresence?.fetchPresence?.();
                // Radar: Erst ab hier ist der Socket serverseitig authentifiziert
                // (ws._chatPhone steht), und radar-* von einem fremden Socket
                // wirft der Server weg. Nach einem Reconnect ist das ausserdem
                // der Moment, an dem wir uns neu anmelden muessen: Die Verbindung
                // IST die Anwesenheit, ein neuer Socket ist ein neuer Auftritt.
                if (msg.datingEnabled) {
                    VCS.datingRadar?.onSocketAuthed?.();
                    VCS.datingFindme?.onSocketAuthed?.();
                }
                // Einladungslink einloesen, falls einer ueber /i/<token> hereinkam.
                // Erst ab hier gibt es eine Session, gegen die /invite/redeem laufen
                // kann. Die Methode prueft selbst, ob ueberhaupt ein Token vorliegt.
                void VCS.chatContacts?.redeemPendingInvite?.();
                // Ruhemodus-Stand dieses Geraets an den Server (falls offline umgeschaltet).
                VCS.notify?.syncQuietMode?.();
                break;

            // Admin hat Dating global oder fuer diesen User umgeschaltet -- sofort
            // greifen, nicht erst beim naechsten Reconnect (siehe /admin/system-config
            // und /admin/user-dating im Backend).
            case 'dating-updated':
                VCS.chatSetup?.applyDatingEnabled?.(!!msg.enabled);
                break;

            // ── Dating-Radar (Praesenz ueber den Socket) ─────
            case 'radar-peer':
                VCS.datingRadar?.onPeer?.(msg);
                break;

            case 'radar-gone':
                VCS.datingRadar?.onGone?.(msg);
                break;

            // ── Dating "Finde mich"-Spiel ────────────────────
            case 'findme-invited':
                VCS.datingFindme?.onInvited?.(msg);
                break;

            case 'findme-pending':
                VCS.datingFindme?.onPending?.(msg);
                break;

            case 'findme-accepted':
                VCS.datingFindme?.onAccepted?.(msg);
                break;

            case 'findme-update':
                VCS.datingFindme?.onUpdate?.(msg);
                break;

            case 'findme-ended':
                VCS.datingFindme?.onEnded?.(msg);
                break;

            case 'findme-busy':
                VCS.datingFindme?.onBusy?.(msg);
                break;

            case 'plan-updated': {
                const oldPlan = s.chatMyPlan;
                s.chatMyPlan = msg.plan || 'free';
                localStorage.setItem('chat-plan', s.chatMyPlan);
                // Plan-Eigenschaften mitziehen, sonst haengt die UI nach einem
                // Tarif-Wechsel am alten Stand (siehe state.js).
                if (msg.planPaid !== undefined) {
                    s.chatPlanPaid = !!msg.planPaid;
                    localStorage.setItem('chat-plan-paid', s.chatPlanPaid ? '1' : '0');
                }
                if (msg.planLiveQuota !== undefined) {
                    s.chatPlanLiveQuota = !!msg.planLiveQuota;
                    localStorage.setItem('chat-plan-live-quota', s.chatPlanLiveQuota ? '1' : '0');
                }
                if (msg.canBuyMinutes !== undefined) {
                    s.chatCanBuyMinutes = !!msg.canBuyMinutes;
                    localStorage.setItem('chat-can-buy-minutes', s.chatCanBuyMinutes ? '1' : '0');
                }
                if (msg.trialEnded) {
                    s.chatTrialEndsAt = 0;
                    localStorage.setItem('chat-trial-ends-at', '0');
                    VCS.ui?.showToast(t('trialEnded'), 'info', 8000);
                }
                // Paketpreise haengen am Tarif -- nach einem Wechsel neu holen.
                if (oldPlan !== s.chatMyPlan) {
                    VCS.chatSetup?.resetMinutePackages?.();
                }
                VCS.chatSetup?.updateSettingsUI();
                VCS.e2e?.updateTranslateToggle();
                if (oldPlan !== s.chatMyPlan && !msg.trialEnded) {
                    // Anzeigename vom Server. Die frueher hier stehende Liste
                    // fuehrte noch 'pro' und 'business' -- Tarife, die es lange
                    // nicht mehr gibt.
                    const shownPlan = s.chatPlanDisplayName || s.chatMyPlan;
                    VCS.ui?.showToast(t('planChanged').replace('{plan}', shownPlan), 'info', 10000);
                }
                break;
            }

            case 'pro-trial-update':
                // Backend (signaling/modules/helpers.js) sendet die Felder als 'used' und 'total'.
                // Earlier code expected long names which produced undefined and broke the UI.
                if (typeof msg.used === 'number') {
                    s.chatProTrialSecondsUsed = msg.used;
                    localStorage.setItem('chat-pro-trial-used', String(s.chatProTrialSecondsUsed));
                }
                if (typeof msg.total === 'number') {
                    s.chatProTrialSecondsTotal = msg.total;
                    localStorage.setItem('chat-pro-trial-total', String(s.chatProTrialSecondsTotal));
                }
                VCS.captions?.updateProTrialUI?.();
                VCS.chatSetup?.updateSettingsUI?.();
                break;

            case 'live-cap-update':
                // Live-Tier monthly cap update: total + multi-lang-group sub-cap.
                // Backend payload: {used,total,groupUsed,groupTotal,periodStart,unlimited}
                s.chatLiveCapSecondsUsedMonth  = msg.used        || 0;
                s.chatLiveCapSecondsTotal      = msg.total       || 0;
                s.chatLiveCapGroupSecondsUsed  = msg.groupUsed   || 0;
                s.chatLiveCapGroupSecondsTotal = msg.groupTotal  || 0;
                s.chatLiveCapPeriodStart       = msg.periodStart || 0;
                s.chatLiveCapUnlimited         = !!msg.unlimited;
                // Gekauftes Guthaben (2026-08-30). Nur uebernehmen, wenn der Server
                // es mitschickt -- ein aelterer Server wuerde den Stand sonst auf 0
                // setzen und der Nutzer saehe seine bezahlten Minuten verschwinden.
                if (msg.extra !== undefined) {
                    s.chatLiveExtraSeconds = msg.extra || 0;
                }
                try {
                    localStorage.setItem('chat-live-cap-used',         String(s.chatLiveCapSecondsUsedMonth));
                    localStorage.setItem('chat-live-cap-total',        String(s.chatLiveCapSecondsTotal));
                    localStorage.setItem('chat-live-cap-group-used',   String(s.chatLiveCapGroupSecondsUsed));
                    localStorage.setItem('chat-live-cap-group-total',  String(s.chatLiveCapGroupSecondsTotal));
                    localStorage.setItem('chat-live-cap-period-start', String(s.chatLiveCapPeriodStart));
                    localStorage.setItem('chat-live-cap-unlimited',    s.chatLiveCapUnlimited ? '1' : '0');
                    localStorage.setItem('chat-live-extra',            String(s.chatLiveExtraSeconds));
                } catch (_) {}
                VCS.chatSetup?.updateSettingsUI?.();
                // Bestaetigung nach einem Kauf. purchasedMinutes setzt der Server nur
                // beim Kauf selbst, nicht bei jedem Cap-Update.
                if (msg.purchasedMinutes) {
                    VCS.ui?.showToast?.(
                        t('minutesPurchased').replace('{min}', String(msg.purchasedMinutes)),
                        'success', 8000
                    );
                    // Nachkauf im Gespraech: Hinweis weg, und falls die
                    // Uebersetzung wegen des Caps stand, wieder anwerfen.
                    VCS.liveCapNotice?.onPurchase?.();
                }
                break;

            // Kontingent wird knapp -- Hinweis mit Kauf-Knopf im laufenden
            // Gespraech. Der Server schickt das einmal je Abschnitt, nicht
            // jede Minute (siehe _liveCapWarned in helpers.js).
            case 'live-cap-low':
                VCS.liveCapNotice?.onLow?.(msg);
                break;

            // Kontingent und Guthaben leer -- die Uebersetzung ist ab hier aus.
            case 'live-cap-expired':
                VCS.liveCapNotice?.onExpired?.(msg);
                VCS.chatSetup?.updateSettingsUI?.();
                break;

            // Minuten teilen (2026-08-31): jemand hat gerade angefangen, mit
            // mir zu teilen. Fluechtiger Toast, keine Chat-Systemnachricht
            // (Gruender-Entscheidung 31.08.) -- ich merke es sonst erst im
            // naechsten Call an den Untertiteln.
            case 'live-share-started':
                VCS.ui?.showToast?.(
                    (t('liveShareStartedNotice') || '').replace('{name}', msg.ownerDisplayName || ''),
                    'info', 6000
                );
                // Freigabe mitten im Gespraech: Aufnahme wieder hochfahren,
                // falls sie mangels Kontingent stillstand.
                VCS.liveCapNotice?.onShareStarted?.();
                break;

            // Das Guthaben des Sponsors ist mitten im Gespraech leer gelaufen,
            // oder der Sponsor hat die Freigabe waehrend des Calls beendet
            // (routes.js DELETE /minutes/share, "sofort wirksam").
            case 'live-share-ended':
                VCS.liveCapNotice?.onShareEnded?.();
                break;

            case 'pro-trial-expired':
                VCS.ui?.showToast?.(t('proTrialExpired'), 'warning', 10000);
                VCS.captions?.onProTrialExpired?.();
                VCS.chatSetup?.updateSettingsUI?.();
                break;

            case 'message-new':
                VCS.chatMessages?.onMessageReceived(msg);
                break;

            // Concierge-Antwort live, Stueck fuer Stueck (2026-10-15). Jedes
            // Paket traegt den GESAMTEN bisherigen Text; die fertige Nachricht
            // kommt danach als message-new mit derselben ID.
            case 'bot-delta':
                VCS.chatMessages?.onBotDelta?.(msg);
                break;

            case 'bot-stream-abort':
                VCS.chatMessages?.onBotStreamAbort?.(msg);
                break;

            case 'message-translated':
                VCS.chatMessages?.onMessageTranslated(msg);
                break;

            case 'message-translation-failed':
                VCS.chatMessages?.onMessageTranslationFailed(msg);
                break;

            // updateMessageStatus liefert die chatId zurueck, unter der die Nachricht
            // wirklich liegt — die Chat-Liste muss dieselbe benutzen, sonst findet sie
            // den Eintrag nicht und die Ampel in der Uebersicht bleibt stehen.
            case 'message-delivered': {
                const delChatId = VCS.chatMessages?.updateMessageStatus(msg.messageId, msg.chatId, 'delivered', null, msg.deliveredAt);
                VCS.chatMessages?.updateChatListStatus(delChatId || msg.chatId, 'delivered');
                break;
            }

            case 'message-read': {
                const readChatId = VCS.chatMessages?.updateMessageStatus(msg.messageId, msg.chatId, 'read');
                VCS.chatMessages?.updateChatListStatus(readChatId || msg.chatId, 'read');
                break;
            }

            // Gruppen-Acks tragen readerPhone (+ allReadMessageIds), 1:1-Acks nicht.
            // Vorher entschied hier VCS.state.chatActiveIsGroup — das beschreibt aber
            // den gerade offenen Chat, nicht den Chat, zu dem der Ack gehoert.
            case 'messages-read-ack': {
                let ackChatId = null;
                if (msg.messageIds) {
                    if (msg.readerPhone) {
                        // Gruppen: per-Reader tracken
                        VCS.chatMessages?.trackGroupReader(msg.chatId, msg.messageIds, msg.readerPhone);
                        // Nur Nachrichten als 'read' (blau) markieren wo ALLE gelesen haben
                        if (msg.allReadMessageIds?.length > 0) {
                            msg.allReadMessageIds.forEach(id => {
                                ackChatId = VCS.chatMessages?.updateMessageStatus(id, msg.chatId, 'read') || ackChatId;
                            });
                        }
                        // Nachrichten wo noch nicht alle gelesen haben → 'delivered' lassen (graue Doppelhäkchen)
                        msg.messageIds.forEach(id => {
                            if (!msg.allReadMessageIds?.includes(id)) {
                                VCS.chatMessages?.ensureMinStatus(id, msg.chatId, 'delivered');
                            }
                        });
                    } else {
                        // 1:1: Direkt als gelesen markieren
                        msg.messageIds.forEach(id => {
                            ackChatId = VCS.chatMessages?.updateMessageStatus(id, msg.chatId, 'read', msg.readAt) || ackChatId;
                        });
                    }
                }
                const ackStatus = (msg.allReadMessageIds?.length > 0 || !msg.readerPhone) ? 'read' : 'delivered';
                VCS.chatMessages?.updateChatListStatus(ackChatId || msg.chatId, ackStatus);
                break;
            }

            case 'messages-read-sync':
                VCS.chatMessages?.handleReadSync?.(msg.chatId);
                break;

            case 'badge-update':
                VCS.notify?.applyBadge?.(msg.count);
                break;

            case 'voice-translation-ready':
                VCS.chatMessages?.onVoiceTranslationReady(msg);
                break;

            case 'video-translation-ready':
                VCS.chatMessages?.onVideoTranslationReady(msg);
                break;

            case 'voicephoto-words':
                VCS.chatVoice?.onVoicephotoWords(msg);
                break;

            case 'video-words':
                VCS.chatVideo?.onVideoWords(msg);
                break;

            case 'chat-typing':
                if (msg.phone && msg.phone !== s.chatMyPhone) {
                    VCS.chatPresence?.setActivity(msg.phone, 'typing');
                    if (msg.chatId === s.chatActiveChatId) {
                        VCS.chatPresence?.showTyping(msg.phone);
                    }
                }
                break;

            case 'chat-recording':
                if (msg.phone && msg.phone !== s.chatMyPhone) {
                    VCS.chatPresence?.setActivity(msg.phone, 'recording', msg.mode);
                    if (msg.chatId === s.chatActiveChatId) {
                        VCS.chatPresence?.showRecordingIndicator(msg.phone, msg.mode);
                    }
                }
                break;

            case 'chat-presence':
                VCS.chatPresence?.update(msg.phone, msg.online, msg.lastSeen);
                break;

            case 'avatar-update':
                // Peer hat Avatar geändert → Chat-Liste + Kontakte aktualisieren
                console.log('[avatar-update] received:', msg.phone, msg.avatarHash);
                if (msg.phone) {
                    s.chatChats.forEach(c => {
                        if (VCS.chat?.peerPhone?.(c) === msg.phone) {
                            c._peerAvatarHash = msg.avatarHash || null;
                        }
                    });
                    VCS.chatMessages?.renderChatList?.();
                    void VCS.chatContacts?.loadContacts?.();
                    // Offenen Chat-Header aktualisieren
                    if (s.chatActiveChatId) {
                        const activeChat = s.chatChats.find(c => c.id === s.chatActiveChatId);
                        if (activeChat && VCS.chat?.peerPhone?.(activeChat) === msg.phone) {
                            const avatarEl = $('#chatDetailAvatar')[0];
                            if (avatarEl) {
                                if (msg.avatarHash) {
                                    avatarEl.classList.add('has-avatar');
                                    avatarEl.innerHTML = `<img src="${VCS.Chat.API_BASE}/profile/avatar/${msg.avatarHash}" alt="" class="chat-detail-avatar-img">`;
                                } else {
                                    avatarEl.classList.remove('has-avatar');
                                    avatarEl.innerHTML = '';
                                    avatarEl.textContent = (activeChat._peerName || '?')[0].toUpperCase();
                                }
                            }
                        }
                    }
                }
                break;

            case 'group-created':
                VCS.chatGroups?.handleGroupCreated?.(msg);
                break;

            case 'group-member-added':
                VCS.chatGroups?.handleGroupMemberAdded?.(msg);
                break;

            case 'group-member-removed':
                VCS.chatGroups?.handleGroupMemberRemoved?.(msg);
                break;

            // Gruppeneinladungen (28.09.): Liste nach der Anmeldung, einzelne
            // neue sofort, zurueckgezogen/beantwortet, Liste "eingeladen" neu.
            case 'group-invites':
                VCS.chatGroups?.handleGroupInvites?.(msg);
                break;

            case 'group-invite':
                VCS.chatGroups?.handleGroupInvite?.(msg);
                break;

            case 'group-invite-cancelled':
                VCS.chatGroups?.handleGroupInviteCancelled?.(msg);
                break;

            case 'group-invites-updated':
                VCS.chatGroups?.handleGroupInvitesUpdated?.(msg);
                break;

            case 'group-updated':
                VCS.chatGroups?.handleGroupUpdated?.(msg);
                break;

            case 'group-deleted':
                VCS.chatGroups?.handleGroupDeleted?.(msg);
                break;

            case 'chat-e2e-changed':
                s.chatE2EEnabled[msg.chatId] = msg.enabled;
                if (s.chatActiveChatId === msg.chatId) {
                    VCS.e2e?.updateTranslateToggle();
                    VCS.e2e?.syncCaptionsFromChat();
                }
                if (msg.changedBy && msg.changedBy !== s.chatMyPhone) {
                    VCS.ui?.showToast(t(msg.enabled ? 'autoTranslateOff' : 'autoTranslateOn'), 'info');
                }
                break;

            case 'chat-partner-deleted': {
                // Peer has deleted their account on the server. Drop the chat from in-memory
                // state so we stop rendering its messages and (more importantly) stop firing
                // media requests for now-gone URLs (which previously caused 404 spam + fail2ban hits).
                // No localStorage/IndexedDB entry is keyed by chatId, so no persistent cleanup needed.
                const deletedChatId = msg.chatId;
                const wasActive = (s.chatActiveChatId === deletedChatId);
                s.chatChats = (s.chatChats || []).filter(c => c.id !== deletedChatId);
                if (s.chatMessages) delete s.chatMessages[deletedChatId];
                if (s.chatE2EEnabled) delete s.chatE2EEnabled[deletedChatId];
                if (s.chatUnreadCounts) delete s.chatUnreadCounts[deletedChatId];
                if (msg.peerPhone && s.chatE2EKeys) delete s.chatE2EKeys[msg.peerPhone];
                if (wasActive) {
                    s.chatActiveChatId   = null;
                    s.chatActiveIsGroup  = false;
                    s.chatEditingMessageId = null;
                    VCS.chat?.clearReply?.();
                    VCS.ui?.showToast?.('Chat-Partner hat seinen Account gelöscht', 'info', 6000);
                    VCS.nav?.switchTab?.('tabChats');
                }
                VCS.chatMessages?.renderChatList?.();
                break;
            }

            case 'chat-deleted': {
                // Chat aus der Liste nehmen (Dating-Match aufgeloest/geblockt). Die
                // Nachrichten bleiben serverseitig -- hier nur die lokale Anzeige raeumen.
                const delChatId = msg.chatId;
                const wasActiveDel = (s.chatActiveChatId === delChatId);
                s.chatChats = (s.chatChats || []).filter(c => c.id !== delChatId);
                if (s.chatMessages) delete s.chatMessages[delChatId];
                if (s.chatUnreadCounts) delete s.chatUnreadCounts[delChatId];
                if (wasActiveDel) {
                    s.chatActiveChatId  = null;
                    s.chatActiveIsGroup = false;
                    VCS.nav?.switchTab?.('tabChats');
                }
                VCS.chatMessages?.renderChatList?.();
                break;
            }

            case 'message-edited': {
                const editMsgs = s.chatMessages[msg.chatId];
                if (editMsgs) {
                    const m = editMsgs.find(x => x.id === msg.messageId);
                    if (m) {
                        m.originalText = msg.newText;
                        m.editedAt = msg.editedAt;
                        m.translatedText = null;
                        if (s.chatActiveChatId === msg.chatId) {
                            VCS.chatMessages?.renderChatMessages();
                        }
                    }
                }
                break;
            }

            case 'message-deleted': {
                const delMsgs = s.chatMessages[msg.chatId];
                if (delMsgs) {
                    const idx = delMsgs.findIndex(x => x.id === msg.messageId);
                    if (idx !== -1) {
                        delMsgs.splice(idx, 1);
                        if (s.chatActiveChatId === msg.chatId) {
                            VCS.chatMessages?.renderChatMessages();
                        }
                    }
                }
                // Chat-Liste Preview aktualisieren
                const lastMsg = delMsgs?.length > 0 ? delMsgs[delMsgs.length - 1] : null;
                if (lastMsg) {
                    const previewText = lastMsg.encrypted
                        ? '\u{1F512} ' + t('e2eNewMessage')
                        : (lastMsg.translatedText || lastMsg.originalText || '\u{1F3A4}');
                    VCS.chatMessages?.updateChatPreview(msg.chatId, previewText, lastMsg.timestamp, lastMsg.senderPhone, lastMsg.status);
                } else {
                    VCS.chatMessages?.updateChatPreview(msg.chatId, '', 0, null, null);
                }
                break;
            }

            case 'message-reaction': {
                const rxMsgs = s.chatMessages[msg.chatId];
                if (rxMsgs) {
                    const m = rxMsgs.find(x => x.id === msg.messageId);
                    if (m) {
                        if (!m.reactions) {
                            m.reactions = {};
                        }
                        if (msg.action === 'added') {
                            if (!m.reactions[msg.emoji]) {
                                m.reactions[msg.emoji] = [];
                            }
                            if (!m.reactions[msg.emoji].includes(msg.phone)) {
                                m.reactions[msg.emoji].push(msg.phone);
                            }
                        } else if (msg.action === 'removed') {
                            if (m.reactions[msg.emoji]) {
                                m.reactions[msg.emoji] = m.reactions[msg.emoji].filter(p => p !== msg.phone);
                                if (m.reactions[msg.emoji].length === 0) {
                                    delete m.reactions[msg.emoji];
                                }
                            }
                        }
                        if (s.chatActiveChatId === msg.chatId) {
                            VCS.chatMessages?.renderChatMessages();
                        }
                    }
                }
                break;
            }

            // ── Eingehender Direktanruf ──────────────────────
            case 'incoming-call':
                if (!s.chatRegistered) {
                    break;
                }
                // Ruhemodus: still ignorieren. Kein Ablehnen senden -- der
                // Anrufer soll nichts merken, andere Geraete klingeln weiter.
                if (VCS.notify?.isQuiet?.()) {
                    console.log('[quiet] Direktanruf ignoriert: ' + msg.callId);
                    break;
                }
                VCS.chatContacts?.showIncomingCallToast(msg);
                break;

            // ── Contact-Request Events ───────────────────────
            case 'contact-request-new':
                s.chatPendingRequests.push({
                                               fromPhone  : msg.fromPhone,
                                               displayName: msg.displayName,
                                           });
                VCS.chatContacts?.updateContactRequestBadge();
                VCS.ui?.showToast(t('contactRequestReceived') + ': ' + (msg.displayName || msg.fromPhone), 'info');
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;

            case 'contact-request-accepted':
                VCS.ui?.showToast(t('contactAccepted') + ': ' + (msg.displayName || msg.fromPhone), 'success');
                void VCS.chatSetup?.loadChats();
                s.chatSentRequests = s.chatSentRequests.filter(r => r.toPhone !== msg.fromPhone);
                VCS.chatContacts?.updateContactRequestBadge();
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;

            case 'contact-request-declined':
                VCS.ui?.showToast(t('contactDeclined') + ': ' + (msg.displayName || msg.toPhone), 'info');
                s.chatSentRequests = s.chatSentRequests.filter(r => r.toPhone !== msg.toPhone);
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;

            case 'contact-request-cancelled':
                s.chatPendingRequests = s.chatPendingRequests.filter(r => r.fromPhone !== msg.fromPhone);
                VCS.chatContacts?.updateContactRequestBadge();
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;

            case 'contact-removed': {
                // Anderer Nutzer hat uns aus seinen Kontakten entfernt
                const otherPhone = msg.fromPhone;
                if (!otherPhone) break;
                console.log('[chat] contact-removed received from:', otherPhone);
                s.chatApprovedContacts.delete(otherPhone);
                s.chatSavedContacts = s.chatSavedContacts.filter(c => c.phoneNumber !== otherPhone);
                localStorage.setItem('chat-contacts', JSON.stringify(s.chatSavedContacts));

                // Falls Chat zum entfernten Peer offen ist: schliessen
                const activeId = s.chatActiveChatId;
                if (activeId) {
                    const activeChat = s.chatChats.find(c => c.id === activeId);
                    const activePeer = activeChat ? VCS.chat?.peerPhone?.(activeChat) : null;
                    const isGroup = activeChat?._isGroup || activeChat?.isGroup;
                    if (activePeer === otherPhone && !isGroup) {
                        s.chatActiveChatId = null;
                        this.sendActiveChat();
                        s.chatActiveIsGroup = false;
                        s.chatEditingMessageId = null;
                        VCS.chat?.clearReply?.();
                        VCS.nav?.switchTab?.('tabChats');
                    }
                }

                VCS.chatMessages?.renderChatList?.();
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;
            }

            case 'contact-name-updated': {
                const local = s.chatSavedContacts.find(c => c.phoneNumber === msg.phoneNumber);
                if (local) {
                    local.displayName = msg.displayName;
                    localStorage.setItem('chat-contacts', JSON.stringify(s.chatSavedContacts));
                }
                s.chatChats.forEach(c => {
                    if (VCS.chat?.peerPhone(c) === msg.phoneNumber) {
                        c._peerName = msg.displayName;
                    }
                });
                VCS.chatMessages?.renderChatList();
                if (!this._view.tabContacts.hasClass('hidden')) {
                    void VCS.chatContacts?.loadContacts();
                }
                break;
            }

            case 'profile-sync': {
                const s = VCS.state;
                if (msg.displayName && msg.displayName !== s.chatMyName) {
                    s.chatMyName = msg.displayName;
                    try { localStorage.setItem('chat-name', msg.displayName); } catch (_) {}
                    const nameInput = $('#profileNameInput')[0];
                    if (nameInput) nameInput.value = msg.displayName;
                    const regInput = $('#regNameInput')[0];
                    if (regInput) regInput.value = msg.displayName;
                    VCS.chatSetup?.updateProfileUI?.();
                    console.log('[signaling] profile-sync: name updated to', msg.displayName);
                }
                if (msg.lang && msg.lang !== s.currentLang) {
                    VCS.i18n?.setLang(msg.lang);
                    console.log('[signaling] profile-sync: lang updated to', msg.lang);
                }
                break;
            }
        }
    }
};
